import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import i18n from 'src/lib/i18n/i18n';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { ImageSliceView } from '@play-co/timestep-core/ui';
import { Feature } from 'src/replicant/analytics';
import uiConfig from 'src/lib/ui/config';
import type { PlayerProfile } from 'src/replicant/friends/friends.ruleset';
import { AssetGroup } from '@play-co/timestep-core';
import { Avatar } from 'src/game/components/shared/Avatar';
import { SpeciesAvatar } from '../components/SpeciesAvatar';
import { getPetSpeciesName } from 'src/replicant/pet/pet.getters';
import { user } from 'src/StateObserver';
import { FriendConfirmAction } from '../friends.analytics.schema';

type CloseHandler = (result: FriendConfirmAction) => void;

export default class PopupFriendsConfirmation extends PopupBasicLazy {
  private buttonCancel: ButtonScaleViewWithText;
  private buttonOk: ButtonScaleViewWithText;
  private close: CloseHandler;
  private playerPhoto!: Avatar;
  private playerName!: LangBitmapFontTextView;
  private speciesName!: LangBitmapFontTextView;
  private speciesAvatar: SpeciesAvatar;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 500,
      darkerBg: false,
      superview: opts.superview,
      close: () => opts.close('dismissed'),
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.POPUP_CONFIRM_REQUEST._,
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    this.message.updateOpts({
      size: 30,
      height: 64,
      width: this.box.style.width - 104,
      x: 52,
      y: 80,
      zIndex: 1,
    });

    // Divider line
    new View({
      superview: this.message,
      width: this.message.style.width,
      y: this.message.style.height + 10,
      height: 4,
      backgroundColor: '#FDC381',
    });

    const playerView = new ImageSliceView({
      superview: this.box,
      x: 25,
      y: this.message.style.y + this.message.style.height + 25,
      width: this.box.style.width - 50,
      height: 180,
      image: 'assets/ui/shared/frames/scroller_panel_blue.png',
      slices: {
        top: 19,
        left: 19,
        right: 22,
        bot: 28,
      },
    });

    this.playerPhoto = new Avatar({
      superview: playerView,
      centerOnOrigin: false,
      width: 128,
      height: 128,
      x: 16,
      y: 25,
    });

    this.speciesAvatar = new SpeciesAvatar({
      superview: playerView,
      centerOnOrigin: true,
      x: this.playerPhoto.style.x + this.playerPhoto.style.width - 20,
      y: this.playerPhoto.style.y + this.playerPhoto.style.height - 20,
      scale: 0.4,
    });

    this.playerName = new LangBitmapFontTextView({
      superview: playerView,
      size: 30,
      font: 'Body',
      color: '#9A7171',
      verticalAlign: 'center',
      width: playerView.style.width - 175,
      height: 48,
      x: this.playerPhoto.style.x + this.playerPhoto.style.width + 10,
      y: this.playerPhoto.style.y + 3,
    });

    this.speciesName = new LangBitmapFontTextView({
      superview: playerView,
      size: 22,
      font: 'Body',
      color: '#7191AB',
      x: this.playerName.style.x,
      y: this.playerName.style.y + 50,
      width: this.playerName.style.width,
      height: 34,
      align: 'left',
    });

    const buttonWidth = 230;
    const buttonHeight = 100;

    this.buttonCancel = new ButtonScaleViewWithText({
      superview: this.box,
      ...uiConfig.buttons.cancel,
      labelOffsetY: -1,
      fontSize: 46,
      font: 'Body',
      width: buttonWidth,
      height: buttonHeight,
      x: (this.box.style.width - buttonWidth) / 2 - 10,
      y: this.box.style.height - 25 - buttonHeight / 2,
      centerOnOrigin: true,
      localeText: () => i18n('basic.cancel'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close('rejected');
      },
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.POPUP_CONFIRM_REQUEST.CLOSE,
      },
    });

    this.buttonOk = new ButtonScaleViewWithText({
      superview: this.box,
      ...uiConfig.buttons.secondary,
      labelOffsetY: -1,
      fontSize: 46,
      font: 'Body',
      width: buttonWidth,
      height: buttonHeight,
      x: (this.box.style.width + buttonWidth) / 2 + 10,
      y: this.box.style.height - 25 - buttonHeight / 2,
      centerOnOrigin: true,
      localeText: () => i18n('basic.ok'),
      onClick: async () => {
        sounds.sfx.play('ok');
        return opts.close('accepted');
      },
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.POPUP_CONFIRM_REQUEST.OK,
      },
    });
  }

  public getAssetsGroup() {
    return new AssetGroup(['assets/ui/shared/frames/backdrop_panel.png']);
  }

  public init(args: { player: PlayerProfile; prompt: 'accept' | 'reject' }) {
    const promptAccept = args.prompt === 'accept';
    super.init({
      title: promptAccept
        ? i18n('friends.acceptConfirmation.title')
        : i18n('friends.rejectConfirmation.title'),
      message: promptAccept
        ? i18n('friends.acceptConfirmation.message')
        : i18n('friends.rejectConfirmation.message'),
    });
    this.playerPhoto.updateImage(args.player.profilePicture);
    this.playerPhoto.setFrame(args.player.frame ?? 'default');
    this.playerName.localeText = () => args.player.name;
    this.speciesName.localeText = () =>
      getPetSpeciesName(user(), args.player.species);
    this.speciesAvatar.setSpecies(args.player.species);
  }
}
